import React, {useEffect} from 'react';

import {withRouter, WithRouterProps} from 'react-router';

import {useAddComment} from 'app/comments/api';
import {CommentForm} from 'app/comments/components/CommentForm';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {DocSidebarPagePanels} from 'app/general/structures/DocSidebarPagePanels';
import {gt} from 'app/utils/i18n';

import {CommentList} from '../CommentList';

import {
  CommentableContentType,
  CommentContentType,
  CommentSerializedType,
} from 'sweet-types';

import type {FilterContentType} from 'app/comments/types';

interface Props extends WithRouterProps {
  contentType: FilterContentType;
  addContentType: CommentableContentType;
  canDeleteComment: boolean;
  objectId: number;
  versionId?: number | string;
  loadAdditionalData?: () => Promise<any>;
  toggleSidebar: () => void;
  mentionsContentType: CommentContentType;
  mentionsObjectId?: number | string;
}

export const GenericCommentsSidebar = withRouter(
  ({
    contentType,
    addContentType,
    canDeleteComment,
    objectId,
    versionId,
    loadAdditionalData,
    toggleSidebar,
    mentionsContentType,
    mentionsObjectId,
    location,
    router,
  }: Props) => {
    const [addComment] = useAddComment();

    useEffect(() => {
      if (loadAdditionalData != null) {
        loadAdditionalData();
      }
    }, []);

    const onAddComment = (comment) => {
      return addComment({
        object_pk:
          versionId != null
            ? typeof versionId === 'string'
              ? parseInt(versionId)
              : versionId
            : objectId,
        parentId: objectId,
        content_type: addContentType,
        comment: comment,
      })
        .unwrap()
        .then((result: CommentSerializedType) => {
          // append newly added comments id to url for easy highlighting
          const {pathname} = location;
          router.replace({
            pathname,
            hash: `#comment${result?.id}`,
          });
        });
    };

    return (
      <DocSidebarPagePanels
        title={gt('Comments')}
        onClose={toggleSidebar}
        footer={
          <CommentForm
            className="d-flex justify-content-between"
            buttonClass="btn-primary"
            onSubmit={onAddComment}
            contentType={mentionsContentType}
            objectId={mentionsObjectId != null ? mentionsObjectId : objectId}
          />
        }
      >
        <CommentList
          canDelete={canDeleteComment}
          contentType={contentType}
          objectId={objectId}
          noResultMessage={
            <NoResultsMessage
              icon="fa-comments"
              head={gt('No comments')}
              onSidebar={true}
            >
              {gt('Use the form below to add a comment')}
            </NoResultsMessage>
          }
        />
      </DocSidebarPagePanels>
    );
  }
);
